import { useEffect, useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";

const ACCEPT = ".jpg,.jpeg,.png,.heic,.heif,.webp,.pdf";
const MAX_MB = 10;

/**
 * <PhotoUploader projectId="..." />
 *
 * Renders an upload drop zone + gallery of uploaded files for the project.
 * Lazily fetches the list once, then keeps local state in sync as uploads succeed.
 */
const PhotoUploader = ({ projectId }) => {
  const { t } = useLang();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/photos`).then((r) => setFiles(r.data)).catch(() => {});
  }, [projectId]);

  const handleFiles = async (fileList) => {
    if (!projectId) { toast.error(t("photo_uploader.toast_no_project")); return; }
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(t("photo_uploader.toast_over_limit", f.name, MAX_MB));
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/projects/${projectId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [{ ...data, created_at: new Date().toISOString() }, ...prev]);
        toast.success(t("photo_uploader.toast_uploaded", f.name));
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("photo_uploader.toast_failed", f.name));
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (fileId, name) => {
    try {
      await api.delete(`/files/${fileId}`);
      setFiles((prev) => prev.filter((x) => x.id !== fileId));
      toast.success(t("photo_uploader.toast_removed", name));
    } catch {
      toast.error(t("photo_uploader.toast_remove_failed"));
    }
  };

  // Build authenticated <img src> URL. We embed the JWT as ?auth= so img tags work.
  const token = typeof window !== "undefined" ? localStorage.getItem("jc_token") : null;
  const fileSrc = (f) => `${API}/files/${f.id}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;

  return (
    <div data-testid="photo-uploader">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className="rounded-lg border-2 border-dashed border-[var(--jc-border-strong)] p-5 bg-[var(--jc-bg)] text-center"
      >
        <div className="flex items-center justify-center gap-2 text-sm text-[var(--jc-text-soft)]">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? t("photo_uploader.uploading") : t("photo_uploader.drag_prompt")}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid="photo-file-input"
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="photo-pick-btn"
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
        >
          {t("photo_uploader.choose_files")}
        </button>
        <div className="mt-2 text-[11px] text-[var(--jc-text-soft)]">{t("photo_uploader.format_hint", MAX_MB)}</div>
      </div>

      {files.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {files.map((f) => {
            const isImage = (f.content_type || "").startsWith("image/");
            return (
              <div key={f.id} className="relative group jc-card overflow-hidden">
                <button
                  onClick={() => remove(f.id, f.original_filename)}
                  data-testid={`photo-remove-${f.id}`}
                  className="absolute top-1.5 right-1.5 z-10 p-1 rounded-full bg-white/95 border border-[var(--jc-border)] text-[var(--jc-text-soft)] hover:text-[var(--jc-red)] opacity-0 group-hover:opacity-100 transition"
                  title={t("photo_uploader.tooltip_remove")}
                >
                  <X size={12} />
                </button>
                {isImage ? (
                  <img
                    src={fileSrc(f)}
                    alt={f.original_filename}
                    className="w-full h-28 object-cover bg-[var(--jc-bg)]"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-28 flex items-center justify-center bg-[var(--jc-bg)] text-[var(--jc-text-soft)]">
                    <FileText size={28} />
                  </div>
                )}
                <div className="px-2 py-1.5 text-[11px] truncate" title={f.original_filename}>
                  {(f.content_type || "").startsWith("image/") ? <ImageIcon size={10} className="inline mr-1" /> : <FileText size={10} className="inline mr-1" />}
                  {f.original_filename}
                </div>
                {f.uploader_role === "field_team" ? (
                  <div
                    className="px-2 pb-1.5 text-[10px] text-[var(--jc-text-soft)] flex items-center gap-1"
                    data-testid={`photo-field-team-badge-${f.id}`}
                  >
                    <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                      Field Team
                    </span>
                    <span className="uppercase tracking-wide">{f.approval_state || "unreviewed"}</span>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;
